// src/components/MealPlanner.jsx  —  ohapotato v2026.10.36
//
// API STRATEGY:
//   Primary: Edamam Recipe Search API (Meal Planner credentials work here per Edamam docs)

import React, { useState, useCallback } from 'react';

// Recipe APIs are proxied through /api/meal-plan-slot — no keys in browser
// Never hardcode keys here — they will be visible in the compiled JS bundle.

// Read pantry items from localStorage to bias meal plan toward ingredients user has
function getPantryIngredients() {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('ohp-pantry-pro-'));
    return keys.flatMap(k => {
      try {
        const items = JSON.parse(localStorage.getItem(k) || '[]');
        return items.map(i => (typeof i === 'string' ? i : i.name)).filter(Boolean);
      } catch { return []; }
    });
  } catch { return []; }
}

// Core weekly staples — ensures ingredient reuse across the week
const WEEKLY_STAPLES = {
  protein: ['chicken', 'eggs', 'beef', 'salmon', 'tofu', 'lentils'],
  veg:     ['spinach', 'broccoli', 'sweet potato', 'tomato', 'onion', 'garlic'],
  grains:  ['rice', 'pasta', 'oats', 'bread', 'quinoa'],
};

function getWeeklyIngredients(pantryItems) {
  const proteins = pantryItems.slice(0, 2).length ? pantryItems.slice(0, 2) : WEEKLY_STAPLES.protein.slice(0, 2);
  const veg      = WEEKLY_STAPLES.veg.slice(0, 3);
  const grain    = WEEKLY_STAPLES.grains[0];
  return { proteins, veg, grain };
}

const DAYS  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const SLOTS = ['breakfast','lunch','dinner'];
const SLOT_LABEL = { breakfast:'Breakfast', lunch:'Lunch', dinner:'Dinner' };
const SLOT_EMOJI = { breakfast:'🍳', lunch:'🥗', dinner:'🍽' };

// Rotating query banks per meal type — avoids the random=true issue
const SLOT_QUERIES = {
  breakfast: ['omelette','pancakes','smoothie bowl','avocado toast','granola','French toast','scrambled eggs','oatmeal','frittata','breakfast burrito','yogurt parfait','waffles','shakshuka','banana bread','muffins'],
  lunch:     ['chicken salad','soup','grain bowl','sandwich','pasta salad','tacos','stir fry','Buddha bowl','wrap','quiche','lentil soup','noodles','flatbread','fried rice','poke bowl'],
  dinner:    ['salmon','roast chicken','pasta','curry','steak','stir fry','risotto','burger','fajitas','chili','stuffed peppers','lasagna','fish tacos','ramen','lamb chops'],
};

const CALORIE_RANGES = {
  breakfast:{ min:250, max:600 },
  lunch:    { min:400, max:750 },
  dinner:   { min:500, max:900 },
};

// Random query picker — uses a session seed so each plan generation is fresh
// but still varies per slot/day within a single generation
let _sessionSeed = 0;
function initSeed() { _sessionSeed = Math.floor(Math.random() * 10000); }
function pickQuery(slot, dayIndex) {
  const queries = SLOT_QUERIES[slot];
  const idx = (_sessionSeed + dayIndex * 7 + SLOTS.indexOf(slot) * 13) % queries.length;
  return queries[idx];
}

// ── Edamam fetch — proxied server-side ──────────────────────────────────────
async function fetchFromEdamam(slot, dayIndex, dietPrefs) {
  const { min, max } = CALORIE_RANGES[slot];
  const query = pickQuery(slot, dayIndex);
  const res = await fetch('/api/meal-plan-slot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot, query, dietPrefs, calorieMin: min, calorieMax: max }),
  });
  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch {}
  if (!res.ok) throw new Error(data.error || `Meal plan API error ${res.status}`);
  return data.meal || null;
}


// ── Spoonacular fallback — proxied server-side ───────────────────────────────
async function fetchFromSpoonacular(slot, dayIndex, dietPrefs) {
  const query = pickQuery(slot, dayIndex);
  const res = await fetch('/api/meal-plan-slot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot, query, dietPrefs, source: 'spoonacular' }),
  });
  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch {}
  if (!res.ok) throw new Error(data.error || `Meal plan API error ${res.status}`);
  return data.meal || null;
}

// ── Fetch one slot — proxied through /api/meal-plan-slot ────────────────────────
async function fetchMealForSlot(slot, dayIndex, dietPrefs = [], weeklyIngs, _pantryLabels = []) {
  // 1. Edamam (primary)
  if (true) {
    try {
      const meal = await fetchFromEdamam(slot, dayIndex, dietPrefs, weeklyIngs, _pantryLabels);
      if (meal) return meal;
    } catch (e) {
      console.warn('[MealPlanner] Edamam failed:', e.message, '— trying Spoonacular');
      // fall through
    }
  }
  // 2. Spoonacular (secondary)
  if (true) {
    try {
      const meal = await fetchFromSpoonacular(slot, dayIndex, dietPrefs);
      if (meal) return meal;
    } catch (e) {
      console.warn('[MealPlanner] Spoonacular failed:', e.message);
      // fall through
    }
  }
  // No further fallbacks
  return null;
}

function isStaple(n) {
  // Match whole-word 'salt' (sea salt, kosher salt, fine salt, etc.)
  if (/\bsalt\b/.test(n)) return true;
  // Match whole-word 'pepper' (black pepper, white pepper, etc.)
  if (/\bpepper\b/.test(n)) return true;
  const STAPLE_FRAGMENTS = [
    'olive oil', 'vegetable oil', 'cooking oil', 'canola oil', 'sunflower oil',
    'baking soda', 'baking powder', 'cornstarch', 'corn starch',
    'plain flour', 'all-purpose flour', 'all purpose flour',
    'water', 'ice water',
  ];
  if (STAPLE_FRAGMENTS.some(s => n.includes(s))) return true;
  return false;
}

function categorise(line) {
  const l = (line || '').toLowerCase();
  if (['chicken','beef','pork','salmon','tuna','shrimp','prawn','egg','eggs','tofu','lamb','turkey','fish','bacon','sausage'].some(w => l.includes(w))) return 'Proteins';
  if (['milk','cheese','yogurt','cream','butter','ghee','cheddar','mozzarella','parmesan','feta'].some(w => l.includes(w))) return 'Dairy';
  if (['lettuce','spinach','tomato','onion','garlic','pepper','carrot','broccoli','zucchini','mushroom','potato','kale','cucumber','apple','lemon','lime','banana','berry','avocado','mango','herb','cilantro','parsley','basil','ginger'].some(w => l.includes(w))) return 'Produce';
  if (['flour','rice','pasta','bread','oat','quinoa','lentil','bean','chickpea','noodle','wrap','tortilla','bagel','pita'].some(w => l.includes(w))) return 'Grains & Legumes';
  return 'Pantry & Spices';
}

const FOOD_WORDS = new Set([
  // proteins
  'chicken','beef','pork','salmon','tuna','shrimp','prawn','egg','eggs','tofu','lamb',
  'turkey','fish','bacon','sausage','steak','mince','mincemeat',
  // dairy
  'milk','cheese','yogurt','cream','butter','ghee','cheddar','mozzarella','parmesan',
  'feta','ricotta','sour',
  // produce
  'onion','onions','garlic','tomato','tomatoes','spinach','broccoli','carrot','carrots',
  'pepper','peppers','mushroom','mushrooms','lemon','lemons','lime','limes','avocado',
  'potato','potatoes','corn','zucchini','kale','lettuce','cucumber','celery','apple',
  'apples','banana','bananas','berry','berries','blueberry','mango','herb','herbs',
  'cilantro','parsley','basil','thyme','rosemary','ginger','scallion','scallions',
  'shallot','shallots','chilli','chili','jalapeño','jalapeno','capsicum','cabbage',
  'cauliflower','asparagus','peas','eggplant','aubergine','beet','beets','radish',
  'arugula','bok','choy','fennel','artichoke','sweet','squash','pumpkin','courgette',
  // grains / legumes
  'rice','pasta','bread','oats','oat','quinoa','lentils','lentil','beans','bean',
  'chickpeas','chickpea','noodles','noodle','flour','tortilla','tortillas','pita',
  'wrap','wraps','bagel','couscous','barley','farro',
  // pantry / condiments
  'oil','olive','soy','sauce','vinegar','salt','pepper','sugar','honey','mustard',
  'ketchup','mayo','mayonnaise','stock','broth','coconut','milk','cream','tomatoes',
  'paste','sauce','tahini','miso','sriracha','worcestershire','tabasco','cumin',
  'paprika','turmeric','coriander','oregano','bay','cinnamon','nutmeg','cardamom',
  'allspice','cloves','clove','chilli','chili','powder','flakes','sesame','seeds',
  'seed','nuts','nut','almond','almonds','walnut','walnuts','cashew','cashews',
  'peanut','peanuts','pine','pistachio','baking','soda','powder','yeast','cornstarch',
  'starch','chocolate','cocoa','vanilla','extract','syrup','jam','jelly','pickle',
  'pickles','olive','olives','capers','anchovies','sardines','herring',
]);

function isFoodWord(word) {
  return FOOD_WORDS.has(word.toLowerCase());
}

function extractCore(line) {
  let s = line
    .toLowerCase()
    .replace(/^[*\-•·]\s*/, '')              // strip bullet chars like * - • ·
    .replace(/\(.*?\)/g, '')                  // remove (optional), (about 3)
    .replace(/,.*$/, '')                      // remove ", finely chopped"
    .replace(/^[\d½¼¾⅓⅔⅛\s/\-.×x]+/, '')  // strip leading quantities
    .replace(/^(cups?|tbsps?|tsps?|tablespoons?|teaspoons?|oz|ounces?|lbs?|pounds?|g|kg|ml|l|cloves?|slices?|pieces?|cans?|bunche?s?|handfuls?|pinch(es)?|dash(es)?|sprigs?|heads?|stalks?|large|medium|small|fresh|dried|chopped|diced|minced|sliced|peeled|grated|shredded|cooked|raw|whole|halved|quartered|crushed|ground|packed|fl|fluid|heaping|leveled?)\s+/gi, '')
    .replace(/^[\d\s/\-.]+/, '')            // catch any remaining leading digits
    .trim();

  // Take first 2 meaningful words
  const words = s.split(/\s+/).slice(0, 2).filter(w => /^[a-zéàüöä]+$/i.test(w));
  return words.join(' ').trim();
}

function stem(word) {
  return word
    .replace(/ies$/i, 'y')
    .replace(/ves$/i, 'f')
    .replace(/oes$/i, 'o')
    .replace(/es$/i, '')
    .replace(/s$/i, '');
}

function consolidateIngredients(lines) {
  const coreToStemmed = new Map(); // stemmedKey → displayName

  for (const raw of lines) {
    const line = (raw || '').trim();
    if (!line || line.length < 2) continue;

    // Skip lines that look like section headers, instructions, or pure numbers
    if (/^\d+[\s/]*$/.test(line)) continue;          // pure quantity: "1/2"
    if (line.split(' ').length > 8) continue;          // too long = instruction sentence
    if (/^[*\-•·\d½¼¾⅓⅔]+\s*[\d/\s]*$/.test(line)) continue; // "* 1/2" etc

    const core = extractCore(line);
    if (!core || core.length < 2) continue;

    // Must contain at least one recognised food word
    const words = core.split(' ');
    if (!words.some(isFoodWord)) continue;

    // Skip universal staples (salt, pepper, oil, etc)
    if (isStaple(core)) continue;

    const stemmedKey = words.map(stem).join(' ');

    // Build a clean display name: unstemmed core, title-cased
    const displayName = core.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    // Keep first occurrence (shortest/cleanest tends to come first after sorting)
    if (!coreToStemmed.has(stemmedKey)) {
      coreToStemmed.set(stemmedKey, displayName);
    }
  }

  // Group into categories and sort
  const shopMap = {};
  for (const displayName of coreToStemmed.values()) {
    const cat = categorise(displayName);
    (shopMap[cat] = shopMap[cat] || []).push(displayName);
  }
  for (const cat of Object.keys(shopMap)) {
    shopMap[cat].sort((a, b) => a.localeCompare(b));
  }
  return shopMap;
}

async function buildPlan(dietPrefs, pantryLabels = []) {
  initSeed(); // fresh random seed every generation
  const pantryItems = pantryLabels.length ? pantryLabels : getPantryIngredients();
  const weeklyIngs  = getWeeklyIngredients(pantryItems);

  // Generate a smaller unique meal set then tile across 7 days.
  // Breakfasts: 3 unique (repeat pattern across 7 days)
  // Lunches:    2 unique (repeat — keeps shopping list tight)
  // Dinners:    3 unique (some repetition is fine)
  const unique = { breakfast: [], lunch: [], dinner: [] };

  const fetchUnique = async (slot, count) => {
    for (let i = 0; i < count; i++) {
      try {
        const meal = await fetchMealForSlot(slot, i, dietPrefs, weeklyIngs, pantryItems);
        if (meal) unique[slot].push(meal);
      } catch (e) {
        if (e.message.includes('credentials') || e.message.includes('plan limit')) throw e;
      }
      await new Promise(r => setTimeout(r, 250));
    }
    // Fallback: if we got fewer than requested, repeat the last one
    while (unique[slot].length < count && unique[slot].length > 0) {
      unique[slot].push(unique[slot][unique[slot].length - 1]);
    }
  };

  await fetchUnique('breakfast', 3);
  await fetchUnique('lunch',     2);
  await fetchUnique('dinner',    3);

  // Tile unique meals across 7 days
  const days = DAYS.map((day, di) => ({
    day,
    meals: {
      breakfast: unique.breakfast[di % unique.breakfast.length] || null,
      lunch:     unique.lunch[di % unique.lunch.length] || null,
      dinner:    unique.dinner[di % unique.dinner.length] || null,
    },
  }));

  // Build consolidated shopping list
  // Strategy: extract the core ingredient name (strip amounts/adjectives),
  // group lines that share the same core ingredient, keep the most descriptive entry.
  const allLines = days.flatMap(d => SLOTS.flatMap(s => d.meals[s]?.ingredients || []));
  const shopMap  = consolidateIngredients(allLines);

  return { days, shoppingList: shopMap };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function MealPlanner({ profile, onClose, pantry = new Set(), onAddToGrocery }) {
  const [plan, setPlan]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [tab, setTab]             = useState('plan');
  const [loadMsg, setLoadMsg]     = useState('');

  const handleGenerate = useCallback(async () => {
    setLoading(true); setError(null); setPlan(null);
    setLoadMsg('Fetching breakfast ideas…');

    // Update loading message as we progress through the week
    const msgs = [
      'Fetching breakfast ideas…',
      'Finding lunch recipes…',
      'Planning dinners…',
      'Almost there — adding the finishing touches…',
    ];
    let msgIdx = 0;
    const msgTimer = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, msgs.length - 1);
      setLoadMsg(msgs[msgIdx]);
    }, 5000);

    try {
      const { INGREDIENTS: INGS } = await import('../data/ingredients');
      const pantryLabels = INGS.filter(i => pantry.has(i.id)).map(i => i.label.toLowerCase());
      const result = await buildPlan(profile?.dietaryPrefs || [], pantryLabels);
      setPlan(result);
      setActiveDay(0);
      setTab('plan');

      // Auto-add missing ingredients to grocery list (deduplicated by hook)
      if (onAddToGrocery) {
        const allIngredients = result.days.flatMap(d =>
          SLOTS.flatMap(s => d.meals[s]?.ingredients || [])
        );
        // Strip amounts/prep to get just the food name
        const foodNames = allIngredients.map(line =>
          line.toLowerCase()
            .replace(/^[\d½¼¾⅓⅔⅛\s/\-.]+/, '')
            .replace(/^(cups?|tbsps?|tsps?|tablespoons?|teaspoons?|oz|ounces?|lbs?|pounds?|g|kg|ml|cloves?|slices?|pieces?|cans?|bunche?s?|large|medium|small|fresh|dried|chopped|diced|minced|sliced|peeled|grated|shredded|cooked|raw|whole|crushed|ground)\s+/gi, '')
            .replace(/\(.*?\)/g, '')
            .replace(/,.*$/, '')
            .trim()
            .split(/\s+/).slice(0, 2).join(' ')
            .trim()
        ).filter(n => n.length > 1);

        // Only add items user does NOT already have in pantry, and skip universal staples
        const needed = [...new Set(foodNames)].filter(name =>
          !pantryLabels.some(p => p.includes(name) || name.includes(p)) &&
          !isStaple(name)
        );
        onAddToGrocery(needed);
      }
    } catch (e) {
      setError(e.message || 'Could not build meal plan — please try again.');
    } finally {
      clearInterval(msgTimer);
      setLoading(false);
      setLoadMsg('');
    }
  }, [profile, pantry, onAddToGrocery]);

  const day = plan?.days?.[activeDay];
  const dayKcal = day ? SLOTS.reduce((s, sl) => s + (day.meals[sl]?.calories || 0), 0) : 0;
  const dietLabel = (profile?.dietaryPrefs || []).join(', ') || 'No restrictions';

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={s.premiumPill}>✦ Premium</span>
            <h2 style={s.title}>Weekly Meal Planner</h2>
            <p style={s.subtitle}>Real recipes from Edamam · {dietLabel}</p>
          </div>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={s.body}>

          {/* ── SPLASH ────────────────────────────────────────── */}
          {!plan && !loading && (
            <div style={s.splash}>
              <div style={s.splashIcon}>🗓️</div>
              <h3 style={s.splashTitle}>Your week, planned in one tap</h3>
              <div style={s.featureList}>
                {[
                  ['📋', '7 days × 3 meals, sourced from Edamam'],
                  ['🥗', `Filtered for: ${dietLabel}`],
                  ['🛒', 'Auto shopping list sorted by aisle'],
                  ['🔗', 'Tap any meal to open the full recipe'],
                  ['⏱', 'Takes about 15 seconds to build'],
                ].map(([icon, text]) => (
                  <div key={text} style={s.featureRow}>
                    <span style={s.featureIcon}>{icon}</span>
                    <span style={s.featureText}>{text}</span>
                  </div>
                ))}
              </div>
              {error && (
                <div style={s.errorBox}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              <button style={s.generateBtn} onClick={handleGenerate}>
                Build my meal plan →
              </button>
            </div>
          )}

          {/* ── LOADING ───────────────────────────────────────── */}
          {loading && (
            <div style={s.loadingWrap}>
              <div style={s.spinnerRing} />
              <p style={s.loadingTitle}>Building your week…</p>
              <p style={s.loadingMsg}>{loadMsg}</p>
              <div style={s.progressDots}>
                {DAYS.map((d, i) => (
                  <div key={d} style={{
                    ...s.dot,
                    background: i < Math.floor(Date.now() / 2500) % 8
                      ? 'var(--accent-gold)' : 'var(--border-base)',
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* ── PLAN ──────────────────────────────────────────── */}
          {plan && !loading && (
            <>
              {/* Tabs */}
              <div style={s.tabBar}>
                {[['plan','📅 Week'],['shopping','🛒 Shopping']].map(([id, label]) => (
                  <button key={id}
                    style={{ ...s.tab, ...(tab === id ? s.tabActive : {}) }}
                    onClick={() => setTab(id)}>
                    {label}
                  </button>
                ))}
              </div>

              {/* ── WEEK VIEW ─────────────────────────────────── */}
              {tab === 'plan' && (
                <>
                  {/* Day selector */}
                  <div style={s.dayBar}>
                    {DAYS.map((d, i) => (
                      <button key={d}
                        style={{ ...s.dayPill, ...(activeDay === i ? s.dayPillActive : {}) }}
                        onClick={() => setActiveDay(i)}>
                        {d.slice(0, 3)}
                      </button>
                    ))}
                  </div>

                  {/* Day header */}
                  <div style={s.dayMeta}>
                    <span style={s.dayName}>{day?.day}</span>
                    {dayKcal > 0 && <span style={s.dayKcal}>~{dayKcal} kcal total</span>}
                  </div>

                  {/* Meal cards */}
                  <div style={s.mealList}>
                    {SLOTS.map(slot => {
                      const meal = day?.meals?.[slot];
                      return (
                        <div key={slot} style={s.mealCard}>
                          <div style={s.mealSlotRow}>
                            <span style={s.mealSlotEmoji}>{SLOT_EMOJI[slot]}</span>
                            <span style={s.mealSlotLabel}>{SLOT_LABEL[slot]}</span>
                            {meal?.calories > 0 && (
                              <span style={s.mealKcal}>{meal.calories} kcal</span>
                            )}
                          </div>
                          {meal ? (
                            <>
                              <p style={s.mealName}>{meal.name}</p>
                              {meal.pantryUsed && meal.pantryUsed.length > 0 && (
                                <div style={s.pantryUsedRow}>
                                  <span style={s.pantryUsedIcon}>🥔</span>
                                  <span style={s.pantryUsedText}>
                                    Uses from your pantry: {meal.pantryUsed.map(p =>
                                      p.charAt(0).toUpperCase() + p.slice(1)
                                    ).join(', ')}
                                  </span>
                                </div>
                              )}
                              <div style={s.mealTagRow}>
                                {meal.prepTime && (
                                  <span style={s.mealTag}>⏱ {meal.prepTime}</span>
                                )}
                                {meal.cuisine && (
                                  <span style={s.mealTag}>{meal.cuisine}</span>
                                )}
                                {(meal.nutrients.protein > 0 || meal.nutrients.carbs > 0) && (
                                  <span style={s.mealTag}>
                                    P {meal.nutrients.protein}g · C {meal.nutrients.carbs}g · F {meal.nutrients.fat}g
                                  </span>
                                )}
                              </div>
                              {meal.url && (
                                <a href={meal.url} target="_blank" rel="noopener noreferrer"
                                  style={s.mealLink}>
                                  View full recipe ↗
                                </a>
                              )}
                            </>
                          ) : (
                            <p style={s.mealNone}>No recipe found for this slot</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ── SHOPPING LIST ─────────────────────────────── */}
              {tab === 'shopping' && (
                <div style={s.shopWrap}>
                  <p style={s.shopNote}>All 7 days combined · tap items to check off as you shop</p>
                  {Object.keys(plan.shoppingList).length === 0 ? (
                    <p style={s.mealNone}>No ingredients found — try regenerating</p>
                  ) : (
                    Object.entries(plan.shoppingList).map(([cat, items]) => (
                      <div key={cat} style={s.shopSection}>
                        <p style={s.shopCat}>{cat}</p>
                        {items.map((item, i) => <ShopItem key={i} label={item} />)}
                      </div>
                    ))
                  )}
                </div>
              )}

              <button style={s.regenBtn} onClick={handleGenerate}>
                ↺ Regenerate plan
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ShopItem({ label }) {
  const [checked, setChecked] = useState(false);
  return (
    <div style={{ ...s.shopItem, opacity: checked ? 0.45 : 1 }}
      onClick={() => setChecked(c => !c)}>
      <div style={{ ...s.shopCheck, ...(checked ? s.shopCheckDone : {}) }}>
        {checked && '✓'}
      </div>
      <span style={{
        fontSize: '13px', lineHeight: '1.45',
        textDecoration: checked ? 'line-through' : 'none',
        color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
      }}>
        {label}
      </span>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 300,
    background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
    display: 'flex', justifyContent: 'flex-end',
    animation: 'fadeUp 0.18s ease',
  },
  panel: {
    width: '100%', maxWidth: '500px', height: '100%',
    background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column',
    boxShadow: 'var(--shadow-lg)', animation: 'slideIn 0.24s ease', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '22px 24px 16px', borderBottom: '1px solid var(--border-base)', flexShrink: 0,
  },
  premiumPill: {
    display: 'inline-block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px',
    color: 'var(--accent-gold)', background: 'var(--accent-gold-bg)',
    padding: '3px 10px', borderRadius: 'var(--radius-pill)',
    marginBottom: '6px', fontFamily: 'var(--font-body)',
  },
  title: {
    fontFamily: 'var(--font-display)', fontSize: '20px',
    color: 'var(--text-primary)', marginBottom: '3px', transition: 'color 0.3s ease',
  },
  subtitle: { fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' },
  closeBtn: {
    background: 'var(--bg-pill)', border: 'none', width: '32px', height: '32px',
    borderRadius: '50%', cursor: 'pointer', fontSize: '14px', color: 'var(--text-muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, fontFamily: 'inherit', marginTop: '2px',
  },
  body: { flex: 1, overflowY: 'auto', padding: '20px 24px' },

  // Splash
  splash: { display: 'flex', flexDirection: 'column' },
  splashIcon: { fontSize: '44px', textAlign: 'center', marginBottom: '12px' },
  splashTitle: {
    fontFamily: 'var(--font-display)', fontSize: '19px',
    color: 'var(--text-primary)', textAlign: 'center', marginBottom: '18px',
  },
  featureList: { marginBottom: '20px' },
  featureRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 0', borderBottom: '1px solid var(--border-base)',
  },
  featureIcon: { fontSize: '16px', width: '22px', textAlign: 'center', flexShrink: 0 },
  featureText: { fontSize: '13.5px', color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' },
  errorBox: {
    padding: '12px 16px', borderRadius: 'var(--radius-md)',
    background: 'rgba(220,38,38,0.07)', color: '#DC2626',
    fontSize: '13px', lineHeight: '1.65', marginBottom: '14px',
    whiteSpace: 'pre-line',
  },
  generateBtn: {
    width: '100%', padding: '14px', border: 'none', borderRadius: 'var(--radius-md)',
    background: 'var(--accent-gold)', color: '#fff',
    fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', transition: 'opacity var(--transition)',
  },

  // Loading
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '72px 16px', gap: '14px' },
  spinnerRing: {
    width: '44px', height: '44px',
    border: '3px solid var(--border-base)', borderTopColor: 'var(--accent-gold)',
    borderRadius: '50%', animation: 'spin 0.85s linear infinite',
  },
  loadingTitle: {
    fontFamily: 'var(--font-display)', fontSize: '19px',
    color: 'var(--text-primary)', textAlign: 'center',
  },
  loadingMsg: { fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' },
  progressDots: { display: 'flex', gap: '6px', marginTop: '4px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', transition: 'background 0.4s ease' },

  // Tabs
  tabBar: {
    display: 'flex', borderBottom: '1px solid var(--border-base)', marginBottom: '16px', flexShrink: 0,
  },
  tab: {
    flex: 1, padding: '10px 0', border: 'none', borderBottom: '2px solid transparent',
    background: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)',
    fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)',
    transition: 'color var(--transition), border-color var(--transition)',
  },
  tabActive: { color: 'var(--text-primary)', borderBottomColor: 'var(--accent-gold)' },

  // Day bar
  dayBar: {
    display: 'flex', gap: '5px', marginBottom: '12px',
    overflowX: 'auto', paddingBottom: '4px',
    // Hide scrollbar visually
    scrollbarWidth: 'none',
  },
  dayPill: {
    flex: '0 0 auto', padding: '6px 12px',
    border: '1.5px solid var(--border-base)', borderRadius: 'var(--radius-pill)',
    background: 'transparent', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: '600',
    color: 'var(--text-muted)', transition: 'all var(--transition)', outline: 'none',
  },
  dayPillActive: {
    background: 'var(--text-primary)', borderColor: 'var(--text-primary)', color: 'var(--bg-base)',
  },
  dayMeta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '12px',
  },
  dayName: { fontFamily: 'var(--font-display)', fontSize: '17px', color: 'var(--text-primary)' },
  dayKcal: { fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' },

  // Meal cards
  mealList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' },
  mealCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-lg)', padding: '14px 16px',
    transition: 'background 0.3s ease, border-color 0.3s ease',
  },
  mealSlotRow: { display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '7px' },
  mealSlotEmoji: { fontSize: '15px' },
  mealSlotLabel: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase',
    color: 'var(--text-muted)', fontFamily: 'var(--font-body)', flex: 1,
  },
  mealKcal: { fontSize: '11px', fontWeight: '600', color: 'var(--accent-orange)', fontFamily: 'var(--font-body)' },
  mealName: {
    fontFamily: 'var(--font-display)', fontSize: '15px',
    color: 'var(--text-primary)', marginBottom: '8px', lineHeight: '1.3',
  },
  mealTagRow: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '7px' },
  mealTag: {
    fontSize: '11px', fontWeight: '500',
    background: 'var(--bg-pill)', color: 'var(--text-muted)',
    padding: '3px 8px', borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-body)',
  },
  mealLink: { fontSize: '12px', fontWeight: '600', color: 'var(--accent-orange)', textDecoration: 'none' },
  mealNone: { fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' },

  // Pantry used banner
  pantryUsedRow: {
    display: 'flex', alignItems: 'flex-start', gap: '6px',
    background: 'var(--accent-gold-bg)',
    border: '1px solid var(--accent-gold)',
    borderRadius: 'var(--radius-sm)',
    padding: '5px 8px', marginBottom: '6px',
  },
  pantryUsedIcon: { fontSize: '13px', flexShrink: 0, marginTop: '1px' },
  pantryUsedText: {
    fontSize: '11px', color: 'var(--accent-gold)',
    fontFamily: 'var(--font-body)', fontWeight: '500', lineHeight: '1.45',
  },

  // Shopping
  shopWrap: { paddingBottom: '16px' },
  shopNote: { fontSize: '12.5px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: '1.5' },
  shopSection: { marginBottom: '20px' },
  shopCat: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase',
    color: 'var(--text-muted)', marginBottom: '8px', fontFamily: 'var(--font-body)',
  },
  shopItem: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '9px 2px', borderBottom: '1px solid var(--border-base)',
    cursor: 'pointer', transition: 'opacity var(--transition)',
  },
  shopCheck: {
    width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, marginTop: '1px',
    border: '1.5px solid var(--border-strong)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '800', transition: 'all var(--transition)',
  },
  shopCheckDone: { background: 'var(--accent-green)', borderColor: 'var(--accent-green)', color: '#fff' },

  regenBtn: {
    display: 'block', width: '100%', marginTop: '8px', padding: '12px',
    border: '1.5px solid var(--border-base)', borderRadius: 'var(--radius-md)',
    background: 'transparent', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600',
    color: 'var(--text-secondary)', transition: 'all var(--transition)',
  },
};
